import SectionTitle from "../SectionTitle";
import SubscriptionDataTable from "./SubscriptionDataTable";
import { getSubscriptionData } from "./utils";
import { SubscriptionContainer } from "./index.styled";
import RatingSection from "./RatingSection";
import PieChart from "./PieChart";
import { useIntersectionObserver } from "../../../hooks/useIntersectionObserver";
import { useState, useEffect } from "react";
import { useDashboard } from "../../../hooks/useDashboard";
import { useDashboardFilter } from "../../../contexts/DashboardFilterContext";

const SubscriptionSource = () => {
  const {
    bySource,
    bySourceDay,
    bySourceWeek,
    ratings,
    ratingsDay,
    ratingsWeek,
    refetchDashboardDataWithPeriod,
    loading,
    monthlySourcesData,
  } = useDashboard();

  const { activeFilter, selectedMonth } = useDashboardFilter();
  const [chartKey, setChartKey] = useState(0);

  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.3,
    rootMargin: "0px",
    triggerOnce: false,
  });

  // Filter monthly data by selected month
  const getMonthlyDataForSelectedMonth = () => {
    if (!monthlySourcesData || selectedMonth === undefined) {
      return null;
    }

    // Create summary for the selected month by filtering by_source data
    const monthlyData = {
      categories: {
        views: 0,
        joins: 0,
        conversion_rate: 0,
        avg_views: 0,
        avg_joins: 0,
        max_views: 0,
        max_joins: 0,
        min_views: 0,
        min_joins: 0,
      },
      direct: {
        views: 0,
        joins: 0,
        conversion_rate: 0,
        avg_views: 0,
        avg_joins: 0,
        max_views: 0,
        max_joins: 0,
        min_views: 0,
        min_joins: 0,
      },
      recommendation: {
        views: 0,
        joins: 0,
        conversion_rate: 0,
        avg_views: 0,
        avg_joins: 0,
        max_views: 0,
        max_joins: 0,
        min_views: 0,
        min_joins: 0,
      },
      search: {
        views: 0,
        joins: 0,
        conversion_rate: 0,
        avg_views: 0,
        avg_joins: 0,
        max_views: 0,
        max_joins: 0,
        min_views: 0,
        min_joins: 0,
      },
      suggestion: {
        views: 0,
        joins: 0,
        conversion_rate: 0,
        avg_views: 0,
        avg_joins: 0,
        max_views: 0,
        max_joins: 0,
        min_views: 0,
        min_joins: 0,
      },
    };

    Object.keys(monthlySourcesData.by_source).forEach((sourceKey) => {
      const sourceData =
        monthlySourcesData.by_source[
          sourceKey as keyof typeof monthlySourcesData.by_source
        ];
      if (!sourceData || !Array.isArray(sourceData)) {
        return;
      }
      const monthData = sourceData.find((item) => {
        const itemDate = new Date(item.date);
        return itemDate.getMonth() + 1 === selectedMonth;
      });

      if (monthData && sourceKey in monthlyData) {
        const key = sourceKey as keyof typeof monthlyData;
        monthlyData[key] = {
          views: monthData.views,
          joins: monthData.joins,
          conversion_rate:
            monthData.joins > 0 ? (monthData.joins / monthData.views) * 100 : 0,
          avg_views: monthData.views,
          avg_joins: monthData.joins,
          max_views: monthData.views,
          max_joins: monthData.joins,
          min_views: monthData.views,
          min_joins: monthData.joins,
        };
      }
    });

    return monthlyData;
  };

  const getDataByFilter = () => {
    if (selectedMonth !== undefined && monthlySourcesData) {
      return getMonthlyDataForSelectedMonth();
    }

    switch (activeFilter) {
      case "day":
        return bySourceDay;
      case "week":
        return bySourceWeek;
      default:
        return bySource;
    }
  };

  const getRatingsByFilter = () => {
    if (selectedMonth !== undefined) {
      return ratings;
    }

    switch (activeFilter) {
      case "day":
        return ratingsDay;
      case "week":
        return ratingsWeek;
      default:
        return ratings;
    }
  };

  const filteredData = getDataByFilter();
  const data = getSubscriptionData(filteredData);

  useEffect(() => {
    if (activeFilter && activeFilter !== "all") {
      if (activeFilter === "day") {
        refetchDashboardDataWithPeriod("today");
      } else {
        refetchDashboardDataWithPeriod(activeFilter);
      }
    }
  }, [activeFilter, refetchDashboardDataWithPeriod]);

  useEffect(() => {
    if (isIntersecting) {
      setChartKey((prev) => prev + 1);
    }
  }, [isIntersecting]);

  useEffect(() => {
    if (selectedMonth !== undefined) {
      setChartKey((prev) => prev + 1);
    }
  }, [selectedMonth, monthlySourcesData]);

  return (
    <SubscriptionContainer>
      <SectionTitle title="פילוח הצטרפות לפי מקור" />
      <SubscriptionDataTable data={data} loading={loading} />
      <div className="content-container">
        <RatingSection ratings={getRatingsByFilter()} />
        <div ref={elementRef}>
          <PieChart data={data} key={chartKey} />
        </div>
      </div>
    </SubscriptionContainer>
  );
};

export default SubscriptionSource;
